import React from 'react';

import {OgImage, Seo} from './seo';
import {buildKeywords} from './keywords';
import {Crumb, JsonLdImage, pageJsonLd, sectionCrumbs} from './json-ld';
import {SectionKey} from './sections';
import {absoluteUrl, OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH, OgKind, ogImageUrl, RULES_NOTE} from './site';
import {entityTitle} from './titles';

type Entity = {
	id: string;
	title: string;
	originalTitle?: string | null;
	alternativeTitles?: Array<string> | null;
	images?: Array<JsonLdImage> | null;
	updatedAt?: string | null;
};

type Props = {
	entity: Entity;
	section: SectionKey;
	/** что это за сущность в заголовке: «заклинание», «чудовище»… */
	kind: string;
	description: string;
	/** каноничный путь страницы */
	path: string;
	og: {kind: OgKind; id?: string};
	/** полный заголовок, если шаблон «Название (Оригинал) — вид» не подходит */
	title?: string;
	keywords?: Array<string | null | undefined | false>;
	/** звенья крошек после раздела; по умолчанию — сама сущность */
	crumbs?: Crumb[];
	/** дописывать в описание «По правилам 2024»: для статей о мире не нужно */
	rulesNote?: boolean;
};

/** Картинка для соцсетей — первая картинка сущности; без неё — сгенерированная карточка */
function entityOgImage(entity: Entity): OgImage | undefined {
	const image = entity.images?.find((item) => item?.path);
	if (!image) return undefined;
	return {
		url: absoluteUrl(`/api/og-photo?path=${encodeURIComponent(image.path)}`),
		width: OG_IMAGE_WIDTH,
		height: OG_IMAGE_HEIGHT,
		type: 'image/jpeg',
		alt: image.description || entity.title,
	};
}

function withRulesNote(description: string): string {
	const text = description.trim();
	if (/2024/.test(text)) return text;
	return `${/[.!?…]$/.test(text) ? text : `${text}.`} ${RULES_NOTE.charAt(0).toUpperCase()}${RULES_NOTE.slice(1)}.`;
}

/** Общая часть SEO карточки сущности: заголовок, описание, ключи, картинка, JSON-LD */
export const EntitySeo: React.FC<Props> = ({
	entity,
	section,
	kind,
	description,
	path,
	og,
	title,
	keywords = [],
	crumbs,
	rulesNote = true,
}) => {
	const text = rulesNote ? withRulesNote(description) : description;
	const fullTitle = title ?? entityTitle(entity.title, entity.originalTitle, kind);
	// у части подклассов в базе вместо пустого списка лежит строка «null»
	const alternatives = (entity.alternativeTitles || []).filter((title) => title && title !== 'null');

	return (
		<Seo
			title={fullTitle}
			description={text}
			canonicalPath={path}
			type="article"
			ogImage={entityOgImage(entity) ?? ogImageUrl(og.kind, og.id ?? entity.id)}
			keywords={buildKeywords(section, entity.title, entity.originalTitle, ...alternatives, ...keywords)}
			jsonLd={pageJsonLd({
				path,
				title: fullTitle,
				description: text,
				pageType: 'Article',
				dateModified: entity.updatedAt,
				crumbs: sectionCrumbs(section, ...(crumbs ?? [{name: entity.title, path}])),
				about: {name: entity.title, alternateName: [entity.originalTitle, ...alternatives], section},
				image: entity.images?.[0],
			})}
		/>
	);
};
